import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AVAILABLE_SCOPES } from "../constants";
import { Card, Button, Input, Badge, IconUpload, TextArea, MediaManager, Modal, Skeleton } from "../components/ui";
import { SelectCompactAntd, InputCompactAntd } from "../components/SelectCompactAntd";
import { Copy, Shield, RefreshCw, AlertTriangle, Star, Plus, Minus, ExternalLink, MessageSquare, Check, PlayCircle, Zap, Globe, Info } from "lucide-react";
import developerAPI from "../api/developer-api";
import { useApp } from "../App";
import toast from "react-hot-toast";
import { Pencil, Trash } from "lucide-react";

function ensureAbsoluteUrl(url) {
  if (!url || typeof url !== "string") return "";
  const t = url.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return "https://" + t;
}

const SCRIPT_TYPE_LABELS = { essential: "Essential", functional: "Functional", analytics: "Analytics", advertising: "Advertising" };
const SCRIPT_PLACEMENT_LABELS = { head: "HTML Head", body_start: "HTML Body start", body_end: "HTML Body end" };
function scriptTypeLabel(v) {
  return SCRIPT_TYPE_LABELS[v] || v;
}
function scriptPlacementLabel(v) {
  return SCRIPT_PLACEMENT_LABELS[v] || v;
}

const PREVIEW_SAMPLE_REVIEWS = [
  { id: "preview-1", reviewer_name: "Alex Chen", created_at: "3 days ago", rating: 5, comment: "Exactly what we needed. Easy to set up and works great with our site. Highly recommend!" },
  { id: "preview-2", reviewer_name: "Jordan Smith", created_at: "1 week ago", rating: 4, comment: "Solid app with good support. Would love to see more customization options in a future update." },
  { id: "preview-3", reviewer_name: "Sam Williams", created_at: "2 weeks ago", rating: 5, comment: "Integration was seamless. Our team uses this daily and it has saved us a lot of time." },
  { id: "preview-4", reviewer_name: "Morgan Taylor", created_at: "1 month ago", rating: 4, comment: "Works as described. Documentation could be clearer but overall a good experience." },
  { id: "preview-5", reviewer_name: "Riley Davis", created_at: "1 month ago", rating: 3, comment: "Decent functionality. Had a small issue during setup but support helped resolve it quickly." },
];
const PREVIEW_SAMPLE_RATING = { average: 4.2, totalRatings: 20, distribution: { 5: 12, 4: 5, 3: 2, 2: 0, 1: 1 } };

export function AppDetail() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { loadApps, user } = useApp();
  const userDisplayName = user ? [user.given_name, user.family_name].filter(Boolean).join(" ").trim() : "";
  const [app, setApp] = useState(null);
  const [stats, setStats] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => location.state?.tab === "settings" ? "settings" : "overview");
  const [listingName, setListingName] = useState("");
  const [listingTagline, setListingTagline] = useState("");
  const [listingDeveloperName, setListingDeveloperName] = useState("");
  const [listingCategory, setListingCategory] = useState("Marketing");
  const [listingPricing, setListingPricing] = useState("Free");
  const [listingVersion, setListingVersion] = useState("1.0.0");
  const [listingStatus, setListingStatus] = useState("development");
  const [listingLongDescription, setListingLongDescription] = useState("");
  const [listingFeatures, setListingFeatures] = useState([]);
  const [listingMedia, setListingMedia] = useState([]);
  const [listingWebsiteUrl, setListingWebsiteUrl] = useState("");
  const [listingContactEmail, setListingContactEmail] = useState("");
  const [listingDocumentationUrl, setListingDocumentationUrl] = useState("");
  const [listingSupportUrl, setListingSupportUrl] = useState("");
  const [listingPrivacyPolicyUrl, setListingPrivacyPolicyUrl] = useState("");
  const [listingTermsUrl, setListingTermsUrl] = useState("");
  const [rotatedSecret, setRotatedSecret] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingStoreListing, setSavingStoreListing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingScriptId, setDeletingScriptId] = useState(null);
  const [scriptToDeleteId, setScriptToDeleteId] = useState(null);

  const [editName, setEditName] = useState("");
  const [editRedirectUris, setEditRedirectUris] = useState([]);
  const [editScopes, setEditScopes] = useState([]);

  const [dashboardPageEnabled, setDashboardPageEnabled] = useState(false);
  const [dashboardPageTab, setDashboardPageTab] = useState("embedded");
  const [dashboardPageEmbeddedUrl, setDashboardPageEmbeddedUrl] = useState("");
  const [dashboardPageExternalUrl, setDashboardPageExternalUrl] = useState("");
  const [dashboardPageSaving, setDashboardPageSaving] = useState(false);

  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoProjects, setDemoProjects] = useState([]);
  const [demoLoading, setDemoLoading] = useState(false);

  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitChangeType, setSubmitChangeType] = useState("minor");
  const [submitChangelogDescription, setSubmitChangelogDescription] = useState("");
  const [submittingForReview, setSubmittingForReview] = useState(false);
  const [unsubmittingForReview, setUnsubmittingForReview] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteVerifyName, setDeleteVerifyName] = useState("");
  const [deleteCountdown, setDeleteCountdown] = useState(null);
  const [deleteCountdownId, setDeleteCountdownId] = useState(0);
  const deleteTimeoutRef = useRef(null);
  const deleteIntervalRef = useRef(null);

  const [userReviews, setUserReviews] = useState([]);
  const [userReviewsLoading, setUserReviewsLoading] = useState(false);
  const [userReviewsRatingFilter, setUserReviewsRatingFilter] = useState(null);

  const [previewTab, setPreviewTab] = useState("overview");
  const [previewSelectedMediaIndex, setPreviewSelectedMediaIndex] = useState(0);

  const previewMediaItems = React.useMemo(() => {
    const raw = Array.isArray(listingMedia) ? listingMedia : [];
    return raw.slice(0, 10).map((m) => {
      if (typeof m === "string") return { type: "image", url: m };
      const url = m?.url;
      if (!url) return null;
      return { type: m?.type === "video" ? "video" : "image", url };
    }).filter(Boolean);
  }, [listingMedia]);

  useEffect(() => {
    if (location.state?.tab === "settings") {
      setActiveTab("settings");
    }
  }, [location.state?.tab]);

  useEffect(() => {
    if (activeTab !== "user reviews" || !appId) return;
    let cancelled = false;
    setUserReviewsLoading(true);
    const params = userReviewsRatingFilter != null ? { rating: userReviewsRatingFilter } : {};
    developerAPI.getAppReviews(appId, params)
      .then((reviews) => {
        if (!cancelled) setUserReviews(Array.isArray(reviews) ? reviews : []);
      })
      .catch(() => {
        if (!cancelled) setUserReviews([]);
      })
      .finally(() => {
        if (!cancelled) setUserReviewsLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, appId, userReviewsRatingFilter]);

  const syncStateFromApp = useCallback((appData) => {
    if (!appData) return;
    const iconUrl = appData.icon_url;
    const iconDisplayUrl =
      iconUrl && typeof iconUrl === "string"
        ? iconUrl + (iconUrl.includes("?") ? "&" : "?") + "_t=" + Date.now()
        : iconUrl;
    setApp({ ...appData, icon_url: iconDisplayUrl });
    const scripts = Array.isArray(appData.embedded_scripts) ? appData.embedded_scripts : [];
    setScripts(scripts);
    const links = Array.isArray(appData.dashboard_links) ? appData.dashboard_links : (appData.dashboard_page_link ? [appData.dashboard_page_link] : []);
    const embedded = links.find((l) => l.type === "embedded");
    const external = links.find((l) => l.type === "external");
    setDashboardPageEmbeddedUrl(embedded?.url ?? "");
    setDashboardPageExternalUrl(external?.url ?? "");
    setDashboardPageEnabled(Boolean((embedded?.url && embedded.url.trim()) || (external?.url && external.url.trim())));
    setEditName(appData.app_name || "");
    setEditRedirectUris(appData.redirect_uris || []);
    setEditScopes(appData.scopes || []);
    setListingName(appData.app_name || "");
    setListingTagline(appData.description || "");
    setListingDeveloperName(appData.developer_name ?? userDisplayName ?? "");
    setListingWebsiteUrl(appData.homepage_url || "");
    setListingLongDescription(appData.long_description ?? "");
    setListingFeatures(Array.isArray(appData.store_listing_features) ? [...appData.store_listing_features] : []);
    setListingMedia(Array.isArray(appData.store_listing_media) ? [...appData.store_listing_media] : []);
    setListingContactEmail(appData.contact_email ?? "");
    setListingDocumentationUrl(appData.documentation_url ?? "");
    setListingSupportUrl(appData.support_url ?? "");
    setListingPrivacyPolicyUrl(appData.privacy_policy_url ?? "");
    setListingTermsUrl(appData.terms_of_service_url ?? "");
    setListingCategory(appData.store_listing_category ?? "Marketing");
    setListingPricing(appData.store_listing_pricing ?? "Free");
    setListingVersion(appData.pending_revision?.proposed_version ?? appData.version ?? "1.0.0");
    setListingStatus(appData.status ?? "development");
  }, [userDisplayName]);

  useEffect(() => {
    const loadApp = async () => {
      try {
        setLoading(true);
        const [appData, statsData] = await Promise.all([
          developerAPI.getApp(appId),
          developerAPI.getAppStats(appId, 30).catch(() => []),
        ]);
        setStats(statsData);
        syncStateFromApp(appData);
      } catch (err) {
        console.error("Failed to load app:", err);
      } finally {
        setLoading(false);
      }
    };
    loadApp();
  }, [appId, userDisplayName, syncStateFromApp]);

  const performDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await developerAPI.deleteApp(appId);
      loadApps();
      navigate("/apps");
    } catch (err) {
      alert(err.response?.data?.error || err.message || "Failed to delete.");
      setDeleting(false);
    }
  }, [appId, loadApps, navigate]);

  useEffect(() => {
    if (deleteCountdownId === 0) return;
    deleteTimeoutRef.current = setTimeout(() => {
      if (deleteIntervalRef.current) clearInterval(deleteIntervalRef.current);
      setDeleteCountdown(null);
      performDelete();
    }, 3000);
    deleteIntervalRef.current = setInterval(() => {
      setDeleteCountdown((prev) => (prev != null && prev > 1 ? prev - 1 : prev));
    }, 1000);
    return () => {
      if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
      if (deleteIntervalRef.current) clearInterval(deleteIntervalRef.current);
    };
  }, [deleteCountdownId, performDelete]);

  const handleDeleteClick = () => {
    setDeleteVerifyName("");
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (!app || (deleteVerifyName || "").trim() !== (app.app_name || "").trim()) return;
    setShowDeleteModal(false);
    setDeleteVerifyName("");
    setDeleteCountdown(3);
    setDeleteCountdownId((c) => c + 1);
  };

  const handleDeleteUndo = () => {
    if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
    if (deleteIntervalRef.current) clearInterval(deleteIntervalRef.current);
    deleteTimeoutRef.current = null;
    deleteIntervalRef.current = null;
    setDeleteCountdown(null);
  };

  if (loading) {
    return (
      <div className="p-8 h-full flex flex-col min-h-0 w-full">
        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center space-x-5">
            <Skeleton className="w-20 h-20 rounded-2xl shrink-0" />
            <div>
              <Skeleton className="h-8 w-48 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-32 rounded-lg" />
            <Skeleton className="h-9 w-28 rounded-lg" />
          </div>
        </div>
        <div className="flex space-x-8 border-b border-slate-200 mb-8">
          {["Overview", "Settings", "Store Listing", "User Reviews"].map((label) => (
            <Skeleton key={label} className="h-4 w-20 pb-4 rounded-none" />
          ))}
        </div>
        <div className="flex-1 min-h-[400px] space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <Skeleton className="h-5 w-36 mb-4" />
                <div className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-10 w-full rounded-lg" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-10 w-full rounded-lg" />
                </div>
              </Card>
              <Card>
                <Skeleton className="h-5 w-24 mb-4" />
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-4 w-full" />
                  ))}
                </div>
              </Card>
            </div>
            <div className="space-y-6">
              <Card>
                <Skeleton className="h-5 w-28 mb-4" />
                <Skeleton className="h-24 w-full rounded-lg" />
              </Card>
              <Card>
                <Skeleton className="h-5 w-20 mb-4" />
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <Skeleton key={i} className="h-4 w-full" />
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!app) return <div className="p-8 text-center text-slate-500">App not found</div>;

  const totalRequests = stats.reduce((acc, s) => acc + (s.requests_count || 0), 0);
  const totalErrors = stats.reduce((acc, s) => acc + (s.errors_count || 0), 0);
  const successRate = totalRequests > 0 ? (((totalRequests - totalErrors) / totalRequests) * 100).toFixed(2) : "100.00";

  function getSubmitForReviewValidation() {
    const errors = [];
    const longDesc = (app.long_description || "").trim();
    if (!longDesc) errors.push("Long description is required");
    const features = Array.isArray(app.store_listing_features) ? app.store_listing_features.filter(Boolean) : [];
    if (features.length === 0) errors.push("At least one key feature is required");
    const media = Array.isArray(app.store_listing_media) ? app.store_listing_media : [];
    if (media.length < 2) errors.push("At least 2 media items (screenshots/videos) are required");
    if (!(app.documentation_url || "").trim()) errors.push("Documentation URL is required");
    if (!(app.support_url || "").trim()) errors.push("Support URL is required");
    if (!(app.privacy_policy_url || "").trim()) errors.push("Privacy policy URL is required");
    if (!(app.terms_of_service_url || "").trim()) errors.push("Terms of service URL is required");
    return { valid: errors.length === 0, errors };
  }

  const submitForReviewValidation = getSubmitForReviewValidation();
  const canSubmitForReview = submitForReviewValidation.valid;

  const handleSave = async (source = "settings") => {
    const nameTrimmed = (editName || "").trim();
    const urisValid = editRedirectUris.map((u) => (u || "").trim()).filter(Boolean);
    if (!nameTrimmed) {
      toast.error("App name is required.");
      return;
    }
    if (urisValid.length === 0) {
      toast.error("At least one redirect URI is required.");
      return;
    }
    setSaving(true);
    try {
      const updated = await developerAPI.updateApp(appId, {
        app_name: nameTrimmed,
        redirect_uris: urisValid,
        scopes: editScopes,
        icon_url: app?.icon_url || undefined,
      });
      if (updated) syncStateFromApp(updated);
      loadApps();
      const isApproved = app?.status === "production" && app?.review_status === "approved";
      toast.success(isApproved
        ? "Changes saved as draft. Submit for review to publish this version."
        : (source === "permissions" ? "Permissions saved." : "Settings saved."));
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDashboardPage = async () => {
    if (dashboardPageEnabled) {
      const url = dashboardPageTab === "embedded" ? dashboardPageEmbeddedUrl?.trim() || "" : dashboardPageExternalUrl?.trim() || "";
      if (!url) {
        toast.error("Dashboard page URL is required.");
        return;
      }
      if (url.includes("://")) {
        try {
          new URL(url);
        } catch {
          toast.error("Please enter a valid URL.");
          return;
        }
      } else if (!url.startsWith("/")) {
        toast.error("URL must be a full URL (e.g. https://...) or a path starting with /.");
        return;
      }
    }
    setDashboardPageSaving(true);
    try {
      if (!dashboardPageEnabled) {
        await developerAPI.deleteAppDashboardPage(appId);
        toast.success("Dashboard page removed.");
      } else {
        const type = dashboardPageTab;
        const url = type === "embedded" ? dashboardPageEmbeddedUrl?.trim() || "" : dashboardPageExternalUrl?.trim() || "";
        await developerAPI.saveAppDashboardPage(appId, { type, url });
        toast.success(app?.status === "production" && app?.review_status === "approved" ? "Changes saved as draft." : "Dashboard page saved.");
      }
      const updated = await developerAPI.getApp(appId);
      if (updated) syncStateFromApp(updated);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Failed to save dashboard page.");
    } finally {
      setDashboardPageSaving(false);
    }
  };

  const handleDeleteScriptClick = (id) => {
    setScriptToDeleteId(id);
  };

  const confirmDeleteScript = async () => {
    const id = scriptToDeleteId;
    if (id == null) return;
    setDeletingScriptId(id);
    try {
      await developerAPI.deleteAppScript(appId, id);
      const updated = await developerAPI.getApp(appId);
      if (updated && Array.isArray(updated.embedded_scripts)) {
        setScripts(updated.embedded_scripts);
        setApp((prev) => (prev ? { ...prev, embedded_scripts: updated.embedded_scripts } : prev));
      } else {
        setScripts((prev) => prev.filter((s) => s.id !== id));
      }
      setScriptToDeleteId(null);
    } catch (err) {
      alert(err.response?.data?.error || err.message || "Failed to delete script.");
    } finally {
      setDeletingScriptId(null);
    }
  };

  const handleRotateSecret = async () => {
    if (!window.confirm("Are you sure? This will invalidate the current secret.")) return;
    try {
      const result = await developerAPI.rotateSecret(appId);
      setRotatedSecret(result.client_secret);
      setActiveTab("overview");
    } catch (err) {
      alert(err.response?.data?.error || err.message || "Failed to rotate secret.");
    }
  };

  const handleSaveStoreListing = async () => {
    setSavingStoreListing(true);
    try {
      const updated = await developerAPI.updateApp(appId, {
        description: listingTagline,
        developer_name: listingDeveloperName,
        store_listing_category: listingCategory,
        store_listing_pricing: listingPricing,
        status: listingStatus,
        long_description: listingLongDescription,
        store_listing_features: listingFeatures.filter(Boolean),
        store_listing_media: listingMedia,
        homepage_url: listingWebsiteUrl || undefined,
        contact_email: listingContactEmail || undefined,
        documentation_url: listingDocumentationUrl || undefined,
        support_url: listingSupportUrl || undefined,
        privacy_policy_url: listingPrivacyPolicyUrl || undefined,
        terms_of_service_url: listingTermsUrl || undefined,
        icon_url: app?.icon_url || undefined,
      });
      if (updated) {
        syncStateFromApp(updated);
      }
      const isApproved = app?.status === "production" && app?.review_status === "approved";
      toast.success(isApproved ? "Changes saved as draft. Submit for review to publish this version." : "Store listing saved.");
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Failed to save store listing.");
    } finally {
      setSavingStoreListing(false);
    }
  };

  const handleOpenDemoModal = async () => {
    setShowDemoModal(true);
    setDemoLoading(true);
    try {
      const projects = await developerAPI.getProjects();
      // Filter for projects where the user is an owner or has admin rights (assuming 'super_admin' or similar check, 
      // but based on API response, we get a list of projects. Let's assume all fetched projects are accessible.
      // We'll filter for standard projects if needed, but for now show all).
      // The user requested: "listing all the sites of the user where the user is the super admin"
      // The API returns projects with a 'role' or similar? Typically /user/projects returns projects the user belongs to.
      // Let's assume the user is owner if they created it or if we check a role property if available. 
      // Looking at backend `getUserProjects` it likely returns project association.
      // For now, I will list all projects returned by the API as eligible for demo.
      setDemoProjects(projects);
    } catch (err) {
      toast.error("Failed to load sites.");
      setShowDemoModal(false);
    } finally {
      setDemoLoading(false);
    }
  };

  const initials = app.app_name.substring(0, 2).toUpperCase();
  const tabs = ["overview", "permissions", "settings", "store listing", "user reviews"];
  const categories = ["CMS", "Directory", "Scripts"];
  const listingCategories = [
    { value: "Marketing", label: "Marketing" },
    { value: "Content", label: "Content" },
    { value: "Utility", label: "Utility" },
    { value: "Analytics", label: "Analytics" },
  ];

  const listingPricingOptions = [
    { value: "Free", label: "Free to use" },
    { value: "Freemium", label: "Freemium" },
    { value: "Free Trial", label: "Free Trial" },
    { value: "Paid", label: "Paid" },
    { value: "Subscription", label: "Subscription" },
    { value: "Ad-supported", label: "Ad-supported" },
  ];

  const previewSelectedMedia = previewMediaItems[previewSelectedMediaIndex] || previewMediaItems[0];
  const previewDisplayName = listingName || app?.app_name || "";
  const previewInitial = (previewDisplayName || "A").charAt(0).toUpperCase();

  return (
    <div className="p-8 h-full flex flex-col min-h-0">
      <div className="flex justify-between items-start mb-8">
        <div className="flex items-center space-x-5">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300 flex items-center justify-center text-2xl font-bold text-slate-600 shadow-sm overflow-hidden">
            {app.icon_url ? <img src={app.icon_url} alt="App Icon" className="w-full h-full object-cover" /> : initials}
          </div>
          <div>
            <div className="flex items-center space-x-3 flex-wrap gap-y-1">
              <h1 className="text-3xl font-bold text-slate-900">{app.app_name}</h1>
            </div>
            <p className="text-slate-500 mt-1 font-medium">ID: {app.id}</p>
          </div>
        </div>


        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={handleOpenDemoModal}
              className="text-xs"
            >
              Test on Demo Website
            </Button>
            {app.review_status === 'pending' ? (
              <Button
                className="text-xs text-white bg-amber-600 hover:bg-amber-700"
                disabled={unsubmittingForReview}
                onClick={async () => {
                  try {
                    setUnsubmittingForReview(true);
                    await developerAPI.unsubmitForReview(appId);
                    toast.success("Review request withdrawn.");
                    const updated = await developerAPI.getApp(appId);
                    setApp(updated);
                    loadApps();
                  } catch (e) {
                    toast.error(e.response?.data?.error || "Failed to withdraw.");
                  } finally {
                    setUnsubmittingForReview(false);
                  }
                }}
              >
                {unsubmittingForReview ? "Withdrawing…" : "Unsubmit"}
              </Button>
            ) : (
              <Button
                className="text-xs text-white bg-blue-600 hover:bg-blue-700"
                disabled={submittingForReview}
                onClick={async () => {
                  if (!canSubmitForReview && app.review_status !== 'approved') {
                    const msg = submitForReviewValidation.errors.length
                      ? submitForReviewValidation.errors.join(" • ")
                      : "Store listing is incomplete.";
                    toast.error(msg);
                    return;
                  }
                  const isUpdate = app.status === "production" && app.review_status === "approved";
                  if (isUpdate) {
                    setShowSubmitModal(true);
                    return;
                  }
                  try {
                    setSubmittingForReview(true);
                    await developerAPI.submitForReview(appId);
                    toast.success("Submitted for review!");
                    const updated = await developerAPI.getApp(appId);
                    syncStateFromApp(updated);
                    loadApps();
                  } catch (e) {
                    const data = e.response?.data;
                    const message = data?.details?.length
                      ? data.details.join("; ")
                      : data?.error || "Failed to submit.";
                    toast.error(message);
                  } finally {
                    setSubmittingForReview(false);
                  }
                }}
              >
                {submittingForReview ? "Submitting…" : "Submit for Review"}
              </Button>
            )}
          </div>
          {app.review_status && (
            <div className={`px-3 py-1 rounded-full text-xs font-bold capitalize border ${app.review_status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
              app.review_status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
              {app.review_status === 'pending' ? 'Under Review' : app.review_status}
            </div>
          )}
        </div>
      </div>

      <div className="flex space-x-8 border-b border-slate-200 mb-8">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-4 text-sm font-medium capitalize transition-all border-b-2 px-1 ${activeTab === tab ? "text-grigora-600 border-grigora-600" : "text-slate-500 border-transparent hover:text-slate-800 hover:border-slate-300"
              }`}
          >
            {tab === "store listing" ? "Store Listing" : tab === "user reviews" ? "User Reviews" : tab}
          </button>
        ))}
      </div>

      {
        activeTab === "overview" && (
          <div className="h-[calc(100vh-220px)] min-h-[400px] overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-10">
              <div className="lg:col-span-2 space-y-8">
                <Card>
                  <h3 className="text-lg font-bold text-slate-900 mb-4">API Credentials</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Client ID</label>
                      <div className="flex mt-1 group">
                        <code className="flex-1 bg-slate-50 border border-slate-200 p-3 rounded-l-md text-slate-700 font-mono text-sm">{app.client_id}</code>
                        <button
                          className="bg-white border-y border-r border-slate-200 px-3 rounded-r-md hover:bg-slate-50 text-slate-400 hover:text-slate-600"
                          onClick={() => navigator.clipboard.writeText(app.client_id)}
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Client Secret</label>
                      {rotatedSecret ? (
                        <div className="mt-1 space-y-2">
                          <div className="flex gap-2">
                            <code className="flex-1 bg-emerald-50 border border-emerald-200 p-3 rounded-l-md text-slate-800 font-mono text-sm break-all">{rotatedSecret}</code>
                            <button
                              type="button"
                              className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 rounded-r-md flex items-center gap-2 font-medium text-sm transition-colors"
                              onClick={() => navigator.clipboard.writeText(rotatedSecret)}
                            >
                              <Copy size={16} />
                              Copy
                            </button>
                          </div>
                          <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-100 inline-flex items-center">
                            <Shield size={12} className="mr-1" />
                            New secret — copy it now. This is the only time it will be shown.
                          </p>
                        </div>
                      ) : (
                        <div className="mt-1">
                          <code className="block bg-slate-50 border border-slate-200 p-3 rounded-md text-slate-700 font-mono text-sm">{"•".repeat(45)}</code>
                          <p className="text-xs text-slate-500 mt-2">Secret is hidden. Use "Rotate Secret" in settings to generate a new one.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-slate-900">Integration Health (30d)</h3>
                    <Badge variant={totalErrors === 0 ? "success" : "warning"}>{totalErrors === 0 ? "Healthy" : "Has Errors"}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Success Rate</p>
                      <p className="text-2xl font-mono text-slate-900">{successRate}%</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Total Requests</p>
                      <p className="text-2xl font-mono text-slate-900">{totalRequests.toLocaleString()}</p>
                    </div>
                  </div>
                </Card>
              </div>
              <div className="space-y-8">
                <Card>
                  <h3 className="text-sm font-bold text-slate-900 mb-4 uppercase tracking-wider">Quick Actions</h3>
                  <div className="space-y-2">
                    <Button variant="secondary" className="w-full justify-start" onClick={() => setActiveTab("store listing")}>Store Listing</Button>
                    <Button variant="secondary" className="w-full justify-start" onClick={() => setActiveTab("user reviews")}>User Reviews</Button>
                    <Button variant="secondary" className="w-full justify-start" onClick={() => setActiveTab("settings")}>Edit App Settings</Button>
                    <Button variant="secondary" className="w-full justify-start" onClick={() => setActiveTab("permissions")}>Manage Permissions</Button>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )
      }

      {
        activeTab === "store listing" && (
          <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-6 md:gap-8 w-full">
            {/* Left: Editor Form - scrollable + fixed Save section at bottom */}
            <div className="flex flex-col min-h-0 overflow-hidden w-full md:w-[35%] md:flex-shrink-0 md:min-w-0">
              <div className="flex-1 min-h-0 overflow-y-auto mr-2">
                <div className="space-y-8 py-4 pl-1 pb-2">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Basic Details</h3>
                    <p className="text-sm text-slate-500 mb-4">Core information displayed in search results.</p>
                    <Card>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch mb-4">
                        <div className="flex items-center justify-center sm:justify-start">
                          <div className="w-[120px] h-[120px] rounded-xl border-2 border-slate-200 bg-slate-50 overflow-hidden shadow-sm flex items-center justify-center flex-shrink-0">
                            {app.icon_url ? (
                              <img src={app.icon_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-2xl font-bold text-slate-400">{initials}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col justify-center min-w-0 space-y-0">
                          <div className="mb-3">
                            <label className="block text-slate-700 text-xs font-semibold mb-1.5 capitalize">App Name</label>
                            <p className="text-sm text-slate-900 font-medium leading-9">{app.app_name}</p>
                          </div>
                          <InputCompactAntd label="Tagline" value={listingTagline} onChange={setListingTagline} placeholder="Short catchy description" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InputCompactAntd label="Developer Name" value={listingDeveloperName} onChange={setListingDeveloperName} placeholder={userDisplayName || "Your name"} />
                        <SelectCompactAntd inputSize="md" label="Category" value={listingCategory} onChange={setListingCategory} options={listingCategories} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <SelectCompactAntd inputSize="md" label="Pricing" value={listingPricing} onChange={setListingPricing} options={listingPricingOptions} />
                        <div>
                          <label className="block text-slate-700 text-xs font-semibold mb-1.5">Version</label>
                          <p className="text-sm text-slate-900 font-medium leading-9" title="Version is set by the system when your app is approved.">{listingVersion}</p>
                          <p className="text-xs text-slate-500 mt-0.5">Version is updated when your update is approved.</p>
                        </div>
                      </div>

                    </Card>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Media</h3>
                    <p className="text-sm text-slate-500 mb-4">Visuals to showcase your app.</p>
                    <Card>
                      <MediaManager
                        label="Gallery (Images & Videos)"
                        value={listingMedia}
                        onChange={setListingMedia}
                        appId={appId}
                        onUpload={async (appId, file) => {
                          const res = await developerAPI.uploadAppMedia(appId, file);
                          return res;
                        }}
                      />
                    </Card>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Product Details</h3>
                    <p className="text-sm text-slate-500 mb-4">Detailed information for the product page.</p>
                    <Card>
                      <TextArea label="Long Description" value={listingLongDescription} onChange={(e) => setListingLongDescription(e.target.value)} className="h-40" subtext="Markdown is supported." />
                      <div className="mb-4">
                        <label className="block text-slate-700 text-xs font-semibold mb-2 capitalize">Key Features</label>
                        {listingFeatures.map((feature, i) => (
                          <div key={i} className="flex gap-2 mb-2">
                            <InputCompactAntd value={feature} onChange={(val) => { const f = [...listingFeatures]; f[i] = val; setListingFeatures(f); }} className="mb-0" />
                            <button type="button" className="text-slate-400 hover:text-red-500 p-2" onClick={() => setListingFeatures(listingFeatures.filter((_, idx) => idx !== i))}>
                              <Minus size={18} />
                            </button>
                          </div>
                        ))}
                        <button type="button" className="text-sm text-grigora-600 font-medium flex items-center mt-2" onClick={() => setListingFeatures([...listingFeatures, ""])}>
                          <Plus size={16} className="mr-1" /> Add Feature
                        </button>
                      </div>
                    </Card>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">Resources & Links</h3>
                    <Card>
                      <InputCompactAntd label="Website URL" value={listingWebsiteUrl} onChange={setListingWebsiteUrl} />
                      <InputCompactAntd label="Contact developer (email or contact page URL)" value={listingContactEmail} onChange={setListingContactEmail} placeholder="support@example.com or https://..." />
                      <InputCompactAntd label="Documentation URL" value={listingDocumentationUrl} onChange={setListingDocumentationUrl} placeholder="https://docs.example.com" />
                      <InputCompactAntd label="Help Guide / Support URL" value={listingSupportUrl} onChange={setListingSupportUrl} placeholder="https://support.example.com" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <InputCompactAntd label="Privacy Policy URL" value={listingPrivacyPolicyUrl} onChange={setListingPrivacyPolicyUrl} placeholder="https://..." />
                        <InputCompactAntd label="Terms of Service URL" value={listingTermsUrl} onChange={setListingTermsUrl} placeholder="https://..." />
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 flex justify-between items-center border-t border-slate-200 bg-slate-50/80 px-4 pt-4 pb-0 rounded-xl shadow-[0_-1px_3px_rgba(0,0,0,0.04)]">
                {app.review_status === 'pending' ? (
                  <span className="text-xs text-slate-500">Saving disabled while under review. Unsubmit to edit.</span>
                ) : (
                  <span />
                )}
                <Button onClick={handleSaveStoreListing} disabled={savingStoreListing || app.review_status === 'pending'}>
                  {savingStoreListing ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </div>

            {/* Right: Preview - scrollable, same row as form on md+ */}
            <div className="flex flex-col min-h-0 min-w-0 w-full md:flex-1 md:min-h-[400px] bg-slate-100 rounded-2xl border border-slate-200 overflow-hidden relative">
              <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-2 flex justify-between items-center text-xs text-slate-500 font-mono">
                <span>Marketplace Preview</span>
                <div className="flex space-x-1">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 md:p-6 bg-white">
                <div className="origin-top max-w-6xl mx-auto" style={{ transform: "scale(0.85)" }}>
                  {/* Matches grigora AppDetails.js layout exactly */}
                  <div className="p-4 md:p-8">
                    {/* Header Section */}
                    <div className="flex flex-col md:flex-row gap-8 mb-10">
                      <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-200 flex items-center justify-center text-gray-500 text-4xl font-bold shadow-lg shrink-0 overflow-hidden">
                        {app.icon_url ? (
                          <img src={app.icon_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span>{previewInitial}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">{previewDisplayName}</h1>
                            <div className="flex items-center gap-4 text-sm text-gray-600 mb-1">
                              {listingDeveloperName && (
                                <>
                                  <span className="flex items-center gap-1">
                                    By <span className="font-semibold text-gray-900">{listingDeveloperName}</span>
                                  </span>
                                  <span className="w-1 h-1 bg-gray-300 rounded-full" />
                                </>
                              )}
                              {listingCategory && (
                                <span className="text-gray-500">{listingCategory}</span>
                              )}
                            </div>
                            <p className="text-sm text-amber-600 flex items-center gap-1.5 mb-4">
                              <Star className="w-4 h-4 fill-current" />
                              <span className="font-medium text-gray-900">{PREVIEW_SAMPLE_RATING.average.toFixed(1)}</span>
                              <span className="text-gray-500">
                                ({PREVIEW_SAMPLE_RATING.totalRatings} {PREVIEW_SAMPLE_RATING.totalRatings === 1 ? "rating" : "ratings"})
                              </span>
                            </p>
                          </div>
                          <div className="flex flex-col gap-3 min-w-[200px]">
                            <div className="h-11 text-base bg-blue-600 border-none shadow-md text-white font-medium rounded-lg px-4 flex items-center justify-center">
                              Add to Site
                            </div>
                            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                              <Check className="w-3 h-3 text-green-600" /> {listingPricingOptions.find((o) => o.value === listingPricing)?.label || listingPricing || "Free Plan"}
                            </div>
                          </div>
                        </div>
                        {listingTagline && (
                          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">{listingTagline}</p>
                        )}
                      </div>
                    </div>

                    {/* Main Content Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                      <div className="lg:col-span-2 space-y-10">
                        {/* Gallery */}
                        {previewMediaItems.length > 0 && (
                          <div className="space-y-4">
                            <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-gray-900 aspect-video w-full">
                              {previewSelectedMedia?.type === "video" ? (
                                <video
                                  key={previewSelectedMediaIndex}
                                  src={previewSelectedMedia.url}
                                  controls
                                  playsInline
                                  className="w-full h-full object-cover"
                                  preload="metadata"
                                >
                                  Your browser does not support the video tag.
                                </video>
                              ) : (
                                <img
                                  src={previewSelectedMedia?.url}
                                  alt="App media"
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            <div className="grid grid-cols-5 sm:grid-cols-5 gap-2">
                              {previewMediaItems.map((item, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => setPreviewSelectedMediaIndex(idx)}
                                  className={`rounded-lg overflow-hidden border-2 aspect-video w-full bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-all ${previewSelectedMediaIndex === idx ? "border-blue-500 ring-2 ring-blue-100" : "border-gray-200 hover:border-gray-300"}`}
                                >
                                  {item.type === "video" ? (
                                    <span className="block w-full h-full relative">
                                      <video
                                        src={item.url}
                                        className="w-full h-full object-cover pointer-events-none"
                                        muted
                                        playsInline
                                        preload="metadata"
                                      />
                                      <span className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                                        <PlayCircle className="w-8 h-8 text-white drop-shadow" />
                                      </span>
                                    </span>
                                  ) : (
                                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Tabs */}
                        <div>
                          <div className="border-b border-gray-200 mb-6">
                            <div className="flex space-x-8">
                              {["Overview", "Reviews"].map((tab) => (
                                <button
                                  key={tab}
                                  type="button"
                                  onClick={() => setPreviewTab(tab.toLowerCase())}
                                  className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${previewTab === tab.toLowerCase() ? "border-black text-black" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}
                                >
                                  {tab}
                                </button>
                              ))}
                            </div>
                          </div>

                          {previewTab === "overview" && (
                            <div className="space-y-8">
                              {listingLongDescription && (
                                <div>
                                  <h3 className="text-xl font-bold text-gray-900 mb-4">About this app</h3>
                                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">{listingLongDescription}</p>
                                </div>
                              )}
                              {listingFeatures.filter(Boolean).length > 0 && (
                                <div>
                                  <h3 className="text-xl font-bold text-gray-900 mb-4">Key Features</h3>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {listingFeatures.filter(Boolean).map((feature, i) => (
                                      <div key={i} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                                          <Zap className="w-3.5 h-3.5" />
                                        </div>
                                        <span className="text-gray-700 font-medium">{typeof feature === "string" ? feature : feature?.text || feature?.title || ""}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {previewTab === "reviews" && (
                            <div className="space-y-10">
                              <div className="w-full">
                                <div className="flex items-baseline gap-2">
                                  <span className="text-4xl font-bold text-gray-900">{PREVIEW_SAMPLE_RATING.average.toFixed(1)}</span>
                                  <div className="flex text-amber-500" aria-hidden>
                                    {[1, 2, 3, 4, 5].map((i) => (
                                      <Star
                                        key={i}
                                        className="w-6 h-6"
                                        fill={i <= Math.round(PREVIEW_SAMPLE_RATING.average) ? "currentColor" : "none"}
                                        stroke="currentColor"
                                        strokeWidth={1.5}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                  {PREVIEW_SAMPLE_RATING.totalRatings} rating{PREVIEW_SAMPLE_RATING.totalRatings !== 1 ? "s" : ""}
                                </p>
                                <div className="mt-6 space-y-2 w-full">
                                  {[5, 4, 3, 2, 1].map((star) => {
                                    const starCount = PREVIEW_SAMPLE_RATING.distribution[star] || 0;
                                    const widthPercent = PREVIEW_SAMPLE_RATING.totalRatings > 0 ? (starCount / PREVIEW_SAMPLE_RATING.totalRatings) * 100 : 0;
                                    const reviewLabel = `${starCount} review${starCount !== 1 ? "s" : ""}`;
                                    return (
                                      <div key={star} className="flex items-center gap-3">
                                        <span className="text-sm text-gray-600 w-7">{star}.0</span>
                                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[80px]">
                                          <div className="h-full bg-amber-400 rounded-full transition-all duration-300" style={{ width: `${widthPercent}%` }} />
                                        </div>
                                        <span className="text-xs text-gray-500 w-16 text-right shrink-0">{reviewLabel}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Reviews</h3>
                                <div className="space-y-6">
                                  {PREVIEW_SAMPLE_REVIEWS.map((r) => (
                                    <div key={r.id} className="p-5 bg-gray-50 rounded-xl border border-gray-100">
                                      <div className="flex items-start justify-between gap-4 mb-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm font-medium shrink-0">
                                            {(r.reviewer_name || "?")[0].toUpperCase()}
                                          </div>
                                          <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{r.reviewer_name}</p>
                                            <p className="text-xs text-gray-500">{r.created_at}</p>
                                          </div>
                                        </div>
                                        {r.rating != null && (
                                          <div className="flex text-amber-500 shrink-0" aria-label={`${r.rating} stars`}>
                                            {[1, 2, 3, 4, 5].map((i) => (
                                              <Star
                                                key={i}
                                                className="w-4 h-4"
                                                fill={i <= r.rating ? "currentColor" : "none"}
                                                stroke="currentColor"
                                                strokeWidth={1.5}
                                              />
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      {r.comment && (
                                        <p className="text-gray-700 text-sm whitespace-pre-wrap leading-relaxed">{r.comment}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Column (Sidebar) */}
                      <div className="space-y-6">
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                          <div className="p-6">
                            <div className="space-y-5">
                              <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">App Information</h4>
                                <div className="space-y-3 text-sm">
                                  {listingWebsiteUrl && (
                                    <div className="flex justify-between items-center py-2 border-b border-gray-50">
                                      <span className="text-gray-600 flex items-center gap-2">
                                        <Globe className="w-4 h-4" /> Website
                                      </span>
                                      <a href={ensureAbsoluteUrl(listingWebsiteUrl)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        Visit Site
                                      </a>
                                    </div>
                                  )}
                                  <div className="flex justify-between items-center py-2 border-b border-gray-50">
                                    <span className="text-gray-600 flex items-center gap-2">
                                      <Info className="w-4 h-4" /> Version
                                    </span>
                                    <span className="font-medium">{listingVersion}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="pt-4">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Support</h4>
                                <div className="space-y-2 flex flex-col items-center text-center">
                                  <div className="flex flex-col items-center gap-2 w-full">
                                    {(listingContactEmail || listingWebsiteUrl) && (
                                      <span
                                        className="w-full max-w-xs px-4 py-2 text-sm font-medium bg-white text-gray-600 border border-gray-300 rounded-lg inline-block cursor-default hover:bg-gray-50"
                                        role="presentation"
                                      >
                                        Contact Developer
                                      </span>
                                    )}
                                  </div>
                                  <span className="w-full max-w-xs px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg inline-block">Report Abuse</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 text-white shadow-lg">
                          <h3 className="font-bold text-lg mb-2">Need Help?</h3>
                          <p className="text-gray-300 text-sm mb-4">
                            Our support team is available 24/7 to assist you with installation.
                          </p>
                          <span className="block w-full py-2.5 text-sm font-medium bg-white text-black rounded-lg border-none text-center">
                            Get Support
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {
        activeTab === "user reviews" && (
          <div className="h-[calc(100vh-220px)] min-h-[400px] overflow-y-auto">
            <div className="max-w-4xl pb-10">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-900">User Reviews</h2>
                <p className="text-sm text-slate-500 mt-0.5">All reviews for this app. Filter by rating to see specific feedback.</p>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  type="button"
                  onClick={() => setUserReviewsRatingFilter(null)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${userReviewsRatingFilter === null
                    ? "bg-grigora-600 text-white border-grigora-600"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                >
                  All
                </button>
                {[5, 4, 3, 2, 1].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setUserReviewsRatingFilter(star)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border flex items-center gap-1.5 ${userReviewsRatingFilter === star
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                      }`}
                  >
                    <Star className="w-4 h-4" fill="currentColor" stroke="currentColor" />
                    {star}
                  </button>
                ))}
              </div>

              {userReviewsLoading ? (
                <div className="text-center py-12 text-slate-500">Loading reviews…</div>
              ) : userReviews.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                  <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No reviews yet.</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {userReviewsRatingFilter != null ? "Try changing the rating filter." : "Reviews from users will appear here."}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userReviews.map((r) => (
                    <Card key={r.id}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex gap-4 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
                            {r.reviewer_photo_url ? (
                              <img src={r.reviewer_photo_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-slate-500 font-medium text-sm">
                                {(r.reviewer_name || r.reviewer_username || "?")[0].toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="font-medium text-slate-900">
                                {r.reviewer_name || r.reviewer_username || "Anonymous"}
                              </span>
                              <span className="text-slate-400 text-sm">
                                {r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : ""}
                              </span>
                            </div>
                            {r.rating != null && (
                              <div className="flex text-amber-500 mb-2" aria-label={`${r.rating} stars`}>
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <Star
                                    key={i}
                                    className="w-4 h-4"
                                    fill={i <= r.rating ? "currentColor" : "none"}
                                    stroke="currentColor"
                                    strokeWidth={1.5}
                                  />
                                ))}
                              </div>
                            )}
                            {r.comment ? (
                              <p className="text-slate-600 text-sm whitespace-pre-wrap leading-relaxed">{r.comment}</p>
                            ) : (
                              <p className="text-slate-400 text-sm italic">No comment</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      }

      {
        activeTab === "settings" && (
          <div className="h-[calc(100vh-220px)] min-h-[400px] overflow-y-auto">
            <div className="space-y-8 max-w-3xl pb-10">
              <Card>
                <h3 className="text-lg font-bold text-slate-900 mb-6 pb-4 border-b border-slate-100">General Configuration</h3>
                <div className="space-y-6">
                  <IconUpload
                    label="App Icon"
                    value={app.icon_url}
                    onUpload={async (file) => {
                      const data = await developerAPI.uploadAppIcon(appId, file);
                      if (data?.icon_url) {
                        const url = data.icon_url;
                        const bust = url.includes("?") ? "&_t=" : "?_t=";
                        setApp((prev) => ({ ...prev, icon_url: url + bust + Date.now() }));
                        toast.success("App icon updated.");
                      }
                      return data?.icon_url;
                    }}
                  />
                  <Input label="App Name" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Required" />
                  <div>
                    <label className="block text-slate-600 text-xs font-bold mb-2 uppercase tracking-wider">Redirect URIs (at least one required)</label>
                    {editRedirectUris.map((uri, idx) => (
                      <div key={idx} className="flex mb-2 gap-2">
                        <input
                          className="flex-1 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-900 text-sm font-mono shadow-sm focus:outline-none focus:ring-1 focus:ring-grigora-500"
                          value={uri}
                          onChange={(e) => {
                            const newUris = [...editRedirectUris];
                            newUris[idx] = e.target.value;
                            setEditRedirectUris(newUris);
                          }}
                        />
                        {editRedirectUris.length > 1 && (
                          <button className="text-red-400 hover:text-red-600 px-2" onClick={() => setEditRedirectUris(editRedirectUris.filter((_, i) => i !== idx))}>
                            &times;
                          </button>
                        )}
                      </div>
                    ))}
                    <button className="text-xs text-grigora-600 hover:text-grigora-700 font-medium mt-1" onClick={() => setEditRedirectUris([...editRedirectUris, ""])}>
                      + Add URI
                    </button>
                  </div>
                  <div className="pt-4 flex justify-between items-center">
                    {app.review_status === 'pending' ? (
                      <span className="text-xs text-slate-500">Saving disabled while under review. Unsubmit to edit.</span>
                    ) : (
                      <span />
                    )}
                    <Button
                      onClick={handleSave}
                      disabled={saving || app.review_status === 'pending' || !(editName || "").trim() || editRedirectUris.map((u) => (u || "").trim()).filter(Boolean).length === 0}
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </div>
              </Card>
              <Card>
                <h3 className="text-lg font-bold text-slate-900 mb-3 pb-4 border-b border-slate-100">Advanced options</h3>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <h4 className="font-medium text-slate-900">Rotate Client Secret</h4>
                    <p className="text-sm text-slate-500">Invalidate the current secret and generate a new one.</p>
                  </div>
                  <Button variant="secondary" onClick={handleRotateSecret} className="flex items-center">
                    <RefreshCw size={14} className="mr-2" /> Rotate
                  </Button>
                </div>
              </Card>

              <Card>
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Embedded scripts</h3>
                    <p className="text-sm text-slate-500 mt-0.5">Add custom JavaScript tags (e.g. analytics, chat widgets) to sites that install your app.</p>
                  </div>
                  <Button variant="secondary" onClick={() => navigate(`/apps/${appId}/scripts/new`)}>
                    Add script
                  </Button>
                </div>
                {scripts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm table-fixed">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-slate-200">
                          <th className="pb-3 pr-4 font-medium" style={{ width: "8ch", minWidth: "8ch" }}>ID</th>
                          <th className="pb-3 pr-4 font-medium" style={{ width: "25ch", minWidth: "25ch", maxWidth: "25ch" }}>Name</th>
                          <th className="pb-3 pr-4 font-medium">Type</th>
                          <th className="pb-3 pr-4 font-medium">Placement</th>
                          <th className="pb-3 text-right font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scripts.map((s) => (
                          <tr key={s.id} className="border-b border-slate-100 last:border-0">
                            <td className="py-3 pr-4 align-middle">
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-700 font-mono text-sm">{s.id}</span>
                                <button
                                  type="button"
                                  onClick={() => navigator.clipboard.writeText(String(s.id))}
                                  className="flex-shrink-0 p-1 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                  title="Copy script ID"
                                >
                                  <Copy size={14} />
                                </button>
                              </div>
                            </td>
                            <td className="py-3 pr-4 align-middle text-slate-900 truncate" style={{ width: "25ch", minWidth: "25ch", maxWidth: "25ch" }} title={s.name}>{s.name}</td>
                            <td className="py-3 pr-4 text-slate-600 align-middle">{scriptTypeLabel(s.script_type)}</td>
                            <td className="py-3 pr-4 text-slate-600 align-middle">{scriptPlacementLabel(s.script_placement)}</td>
                            <td className="py-3 text-right align-middle">
                              <div className="flex items-center justify-end gap-3">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/apps/${appId}/scripts/${s.id}`)}
                                  className="flex-shrink-0 p-2 rounded-md text-grigora-600 hover:text-grigora-700 hover:bg-grigora-50 font-medium transition-colors"
                                  title="Edit script"
                                >
                                  <Pencil size={14}/>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteScriptClick(s.id)}
                                  disabled={deletingScriptId === s.id}
                                  className="flex-shrink-0 p-2 rounded-md text-red-500 hover:text-red-600 hover:bg-red-50 font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                  title="Delete script"
                                >
                                  <Trash size={14}/>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No embedded scripts yet. Click “Add script” to add one.</p>
                )}
              </Card>

              <Card>
                <div className="flex items-center justify-between mb-4 pb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Dashboard Page</h3>
                    <p className="text-sm text-slate-500 mt-0.5">Configure an embedded link or external URL for the app dashboard.</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={dashboardPageEnabled}
                    onClick={() => setDashboardPageEnabled(!dashboardPageEnabled)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-grigora-500 focus:ring-offset-2 ${dashboardPageEnabled ? "bg-grigora-600" : "bg-slate-200"
                      }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${dashboardPageEnabled ? "translate-x-5" : "translate-x-1"
                        }`}
                    />
                  </button>
                </div>
                {dashboardPageEnabled && (
                  <>
                    <div className="flex border-b border-slate-200 mb-4">
                      {["embedded", "external"].map((tab) => (
                        <button
                          key={tab}
                          type="button"
                          onClick={() => setDashboardPageTab(tab)}
                          className={`pb-3 px-4 text-sm font-medium border-b-2 -mb-px ${dashboardPageTab === tab
                            ? "text-grigora-600 border-grigora-600"
                            : "text-slate-500 border-transparent hover:text-slate-700"
                            }`}
                        >
                          {tab === "embedded" ? "Embedded Link" : "External URL"}
                        </button>
                      ))}
                    </div>
                    {dashboardPageTab === "embedded" && (
                      <div className="mb-4">
                        <label className="block text-slate-700 text-xs font-semibold mb-2 uppercase tracking-wider">Link</label>
                        <input
                          type="url"
                          value={dashboardPageEmbeddedUrl}
                          onChange={(e) => setDashboardPageEmbeddedUrl(e.target.value)}
                          placeholder="https://..."
                          className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-900 text-sm focus:outline-none focus:ring-1 focus:ring-grigora-500"
                        />
                      </div>
                    )}
                    {dashboardPageTab === "external" && (
                      <div className="mb-4">
                        <label className="block text-slate-700 text-xs font-semibold mb-2 uppercase tracking-wider">Link</label>
                        <input
                          type="url"
                          value={dashboardPageExternalUrl}
                          onChange={(e) => setDashboardPageExternalUrl(e.target.value)}
                          placeholder="https://..."
                          className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-900 text-sm focus:outline-none focus:ring-1 focus:ring-grigora-500"
                        />
                      </div>
                    )}
                  </>
                )}
                <div className="flex justify-between items-center pt-2">
                  {app.review_status === 'pending' ? (
                    <span className="text-xs text-slate-500">Saving disabled while under review. Unsubmit to edit.</span>
                  ) : (
                    <span />
                  )}
                  <Button onClick={handleSaveDashboardPage} disabled={dashboardPageSaving || app.review_status === 'pending'}>
                    {dashboardPageSaving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </Card>

              <div className="border border-red-200 rounded-xl bg-red-50 p-6">
                <h3 className="text-lg font-bold text-red-700 mb-2 flex items-center">
                  <AlertTriangle size={20} className="mr-2" /> Danger Zone
                </h3>
                <p className="text-sm text-red-600/80 mb-6">Once you delete an app, there is no going back. Please be certain.</p>
                <div className="flex justify-end">
                  <Button variant="danger" onClick={handleDeleteClick} disabled={deleting}>
                    {deleting ? "Deleting..." : "Delete App"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {
        activeTab === "permissions" && (
          <div className="h-[calc(100vh-220px)] min-h-[400px] flex flex-col min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="space-y-6 pb-10">
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-md shadow-sm">
                  <div className="flex">
                    <Shield className="text-blue-600 mt-1 mr-3 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="text-blue-800 font-medium">Principle of Least Privilege</h4>
                      <p className="text-sm text-blue-600 mt-1">Only request scopes that are absolutely necessary for your application's functionality.</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-8">
                  {categories.map((category) => {
                    const categoryScopes = AVAILABLE_SCOPES.filter((s) => s.category === category);
                    if (categoryScopes.length === 0) return null;
                    return (
                      <div key={category}>
                        <h3 className="text-lg font-bold text-slate-900 mb-3 border-b border-slate-200 pb-2">{category} API</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {categoryScopes.map((scope) => {
                            const isEnabled = editScopes.includes(scope.id);
                            return (
                              <div
                                key={scope.id}
                                className={`p-4 rounded-lg border flex items-start space-x-3 transition-colors cursor-pointer ${isEnabled ? "bg-white border-grigora-500 shadow-sm ring-1 ring-grigora-500/20" : "bg-slate-50 border-slate-200 opacity-80 hover:opacity-100 hover:bg-white hover:shadow-sm"
                                  }`}
                                onClick={() => {
                                  if (isEnabled) setEditScopes(editScopes.filter((s) => s !== scope.id));
                                  else setEditScopes([...editScopes, scope.id]);
                                }}
                              >
                                <input type="checkbox" checked={isEnabled} readOnly className="mt-1 h-4 w-4 rounded border-slate-300 text-grigora-600 focus:ring-grigora-500" />
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-mono font-bold text-slate-900">{scope.id}</span>
                                    {scope.risk === "high" && <Badge variant="warning">High Risk</Badge>}
                                  </div>
                                  <p className="text-sm text-slate-500 mt-1">{scope.description}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 flex justify-between items-center border-t border-slate-200 bg-slate-50/80 px-4 pt-4 pb-0 rounded-xl shadow-[0_-1px_3px_rgba(0,0,0,0.04)]">
              {app.review_status === 'pending' ? (
                <span className="text-xs text-slate-500">Saving disabled while under review. Unsubmit to edit.</span>
              ) : (
                <span />
              )}
              <Button onClick={() => handleSave("permissions")} disabled={saving || app.review_status === 'pending'}>{saving ? "Saving…" : "Save Permissions"}</Button>
            </div>
          </div>
        )
      }
      {
        showDemoModal && (
          <Modal
            isOpen={showDemoModal}
            onClose={() => setShowDemoModal(false)}
            title="Select a Demo Site"
          >
            {demoLoading ? (
              <div className="flex justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              </div>
            ) : demoProjects.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-slate-500 mb-4">Choose a site to test your app. You must be an admin of the site.</p>
                {demoProjects.map((project) => (
                  <div
                    key={project.id}
                    className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer flex justify-between items-center transition-colors group"
                    onClick={() => {
                      const demoUrl = `http://localhost:2707/site/${project.id}/app/${appId}?dev=true`;
                      window.open(demoUrl, "_blank");
                      setShowDemoModal(false);
                    }}
                  >
                    <div>
                      <h4 className="font-medium text-slate-900 group-hover:text-grigora-600 transition-colors">{project.name || "Untitled Site"}</h4>
                      <p className="text-xs text-slate-500 mt-0.5">ID: {project.id}</p>
                    </div>
                    <ExternalLink size={16} className="text-slate-400 group-hover:text-grigora-500" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500 mb-4">No available sites found.</p>
                <div className="flex justify-center">
                  <Button onClick={() => window.open("http://localhost:2707/start/new", "_blank")}>Create New Site</Button>
                </div>
              </div>
            )}
          </Modal>
        )
      }
      {deleteCountdown != null && deleteCountdown > 0 && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-900 px-4 py-3 shadow-lg flex items-center justify-center gap-4 flex-wrap">
          <span className="font-medium">App will be deleted in {deleteCountdown} second{deleteCountdown !== 1 ? "s" : ""}. This cannot be undone.</span>
          <Button variant="secondary" onClick={handleDeleteUndo} className="bg-white/90 hover:bg-white border-amber-700/30">
            Undo
          </Button>
        </div>
      )}
      {showDeleteModal && app && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete app?"
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              To confirm, type the exact app name: <strong className="text-slate-900">{app.app_name}</strong>
            </p>
            <Input
              label="App name"
              value={deleteVerifyName}
              onChange={(e) => setDeleteVerifyName(e.target.value)}
              placeholder={app.app_name}
              className="font-mono"
            />
            <p className="text-xs text-slate-500">This action cannot be undone. You will have 3 seconds to undo after confirming.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteConfirm}
                disabled={(deleteVerifyName || "").trim() !== (app.app_name || "").trim()}
              >
                Delete app
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {scriptToDeleteId != null && (
        <Modal
          isOpen={scriptToDeleteId != null}
          onClose={() => !deletingScriptId && setScriptToDeleteId(null)}
          title="Delete embedded script?"
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Delete this embedded script? This cannot be undone.
              {(() => {
                const script = scripts.find((s) => s.id === scriptToDeleteId);
                return script?.name ? (
                  <span className="block mt-1 font-medium text-slate-900">Script: {script.name}</span>
                ) : null;
              })()}
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setScriptToDeleteId(null)} disabled={!!deletingScriptId}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDeleteScript} disabled={!!deletingScriptId}>
                {deletingScriptId === scriptToDeleteId ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {showSubmitModal && (
        <Modal
          isOpen={showSubmitModal}
          onClose={() => !submittingForReview && setShowSubmitModal(false)}
          title="What kind of change is this?"
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Choose how the version will be updated when this update is approved.</p>
            <div className="flex gap-3 flex-wrap">
              {["major", "minor", "patch"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setSubmitChangeType(t)}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium capitalize transition-colors ${
                    submitChangeType === t
                      ? "border-grigora-500 bg-grigora-50 text-grigora-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Major: breaking changes. Minor: new features. Patch: fixes and small updates.
            </p>
            <div>
              <label className="block text-slate-700 text-xs font-semibold mb-1.5">Changelog description (optional)</label>
              <textarea
                value={submitChangelogDescription}
                onChange={(e) => setSubmitChangelogDescription(e.target.value)}
                placeholder="Brief summary of changes..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowSubmitModal(false)} disabled={submittingForReview}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    setSubmittingForReview(true);
                    const iconUrlClean = app?.icon_url ? String(app.icon_url).replace(/\?.*$/, "").trim() || undefined : undefined;
                    await developerAPI.submitForReview(appId, {
                      change_type: submitChangeType,
                      changelog_description: submitChangelogDescription.trim() || undefined,
                      icon_url: iconUrlClean,
                    });
                    toast.success("Update submitted for review!");
                    setShowSubmitModal(false);
                    setSubmitChangelogDescription("");
                    const updated = await developerAPI.getApp(appId);
                    syncStateFromApp(updated);
                    setListingVersion(updated.pending_revision?.proposed_version ?? updated.version ?? "1.0.0");
                    loadApps();
                  } catch (e) {
                    const data = e.response?.data;
                    const message = data?.details?.length ? data.details.join("; ") : data?.error || "Failed to submit.";
                    toast.error(message);
                  } finally {
                    setSubmittingForReview(false);
                  }
                }}
                disabled={submittingForReview}
              >
                {submittingForReview ? "Submitting…" : "Submit for Review"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div >
  );
}
